/**
 * Quantity offer picker for the product form.
 *
 * Prices are rendered by Liquid. This element only keeps the product form's quantity
 * in step with the chosen offer, so the store's automatic discounts apply in the cart.
 */
class VolumePicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  get #quantityInput() {
    const scope = this.closest('.shopify-section') ?? document;
    const form = scope.querySelector('form[action*="/cart/add"]');
    if (!form) return null;

    let input = form.querySelector('input[name="quantity"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      form.append(input);
    }
    return /** @type {HTMLInputElement} */ (input);
  }

  #onChange = (/** @type {Event} */ event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement) {
      const radio = target.closest('.volume-picker__option')?.querySelector('input[type="radio"]');
      if (radio instanceof HTMLInputElement) radio.checked = true;
    }
    this.#sync();
  };

  #sync() {
    const option = this.querySelector('.volume-picker__option:has(input[type="radio"]:checked)');
    if (!option) return;

    const select = option.querySelector('select');
    const choice = select ? select.selectedOptions[0] : null;
    const source = choice ?? option;
    const { quantity = '1', price = '', compareAt = '', savings = '' } = /** @type {HTMLElement} */ (source).dataset;

    if (choice) {
      this.#fill(option, 'price', price);
      this.#fill(option, 'compare-at', compareAt);
    }

    const savingsLine = this.querySelector('[data-volume-picker-savings]');
    this.#fill(this, 'savings-amount', savings);
    savingsLine?.toggleAttribute('hidden', savings === '');

    const input = this.#quantityInput;
    if (input && input.value !== quantity) {
      input.value = quantity;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * @param {Element} scope
   * @param {string} name
   * @param {string} value
   */
  #fill(scope, name, value) {
    const element = scope.querySelector(`[data-volume-picker-${name}]`);
    if (element) element.textContent = value;
  }
}

if (!customElements.get('volume-picker')) {
  customElements.define('volume-picker', VolumePicker);
}
